/**
 * ColumnGeneratedFields — pure presentational form section for the
 * computed / generated-column surface (DSE2 §3.A3 / Slice A3).
 *
 *  • Expression textarea (verbatim text — opaque to the resolver).
 *  • Persistence radio (`stored` | `virtual`) gated per engine via
 *    `isGeneratedPersistenceSupported`. Disabled radios surface the
 *    documented per-engine tooltip via `getGeneratedPersistenceTooltip`.
 *  • Per-engine version note (sqlite 3.31.0+ requirement) via
 *    `getGeneratedEngineVersionNote`.
 *
 * State is owned by the parent. The `disabled` flag lets the parent reflect
 * mutual exclusivity with identity / default — when a generated expression is
 * not in use the textarea + radios stay rendered but inert (preserves layout
 * and avoids form-jump on toggle).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice A3.
 */
import type { SqlEngine } from '../../../../sql-grammar/model';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/radio-group';
import {
  getGeneratedEngineVersionNote,
  getGeneratedPersistenceTooltip,
  isGeneratedPersistenceSupported,
  type GeneratedPersistenceMode,
} from '../../edit/edits/setColumnGenerated';

export interface ColumnGeneratedFieldsProps {
  readonly engine: SqlEngine;
  readonly testId: string;
  readonly expr: string;
  readonly setExpr: (next: string) => void;
  readonly persistence: GeneratedPersistenceMode;
  readonly setPersistence: (next: GeneratedPersistenceMode) => void;
  /** True when the section is mutually exclusive with another form section
   *  (e.g. identity is on); the textarea + radios render but accept no input. */
  readonly disabled?: boolean;
}

const PERSISTENCE_OPTIONS: ReadonlyArray<{
  readonly mode: GeneratedPersistenceMode;
  readonly label: string;
}> = [
  { mode: 'stored', label: 'Stored' },
  { mode: 'virtual', label: 'Virtual' },
];

export function ColumnGeneratedFields(props: ColumnGeneratedFieldsProps) {
  const { engine, testId, expr, setExpr, persistence, setPersistence, disabled } = props;
  const versionNote = getGeneratedEngineVersionNote(engine);

  return (
    <fieldset
      className="flex flex-col gap-1"
      data-testid={`${testId}-section`}
      disabled={disabled}
    >
      <legend className="text-xs">Generated (computed) column</legend>
      <Label htmlFor={`${testId}-expr`} className="text-[10px] text-muted-foreground">
        Expression
      </Label>
      <Input
        id={`${testId}-expr`}
        data-testid={`${testId}-expr`}
        value={expr}
        onChange={(e) => setExpr(e.target.value)}
        placeholder="price * qty / LOWER(email)"
        className="h-8 font-mono text-xs"
      />
      <RadioGroup
        value={persistence}
        onValueChange={(v) => setPersistence(v as GeneratedPersistenceMode)}
        className="flex items-center gap-2 text-xs"
      >
        {PERSISTENCE_OPTIONS.map(({ mode, label }) => {
          const supported = isGeneratedPersistenceSupported(engine, mode);
          const tooltip = supported ? undefined : getGeneratedPersistenceTooltip(engine, mode);
          return (
            <label key={mode} className="flex items-center gap-1" title={tooltip}>
              <RadioGroupItem
                value={mode}
                disabled={!supported || disabled}
                data-testid={`${testId}-persistence-${mode}`}
              />
              <span className={supported ? '' : 'text-muted-foreground'}>{label}</span>
            </label>
          );
        })}
      </RadioGroup>
      {versionNote ? (
        <span
          className="text-[10px] text-muted-foreground"
          data-testid={`${testId}-version-note`}
        >
          {versionNote}
        </span>
      ) : null}
    </fieldset>
  );
}
